import type { OwnerWithOwnershipData } from "../../lib/models/leased-tag.server.ts"

import * as UI from "@iyk/ui"
import * as React from "react"
import * as Dialog from "../../lib/ui/dialog.tsx"

import { toSentenceCase } from "@iyk/string"
import { Link, useParams } from "@remix-run/react"
import { useIykUser } from "../../lib/hooks/use-iyk-user.ts"
import { useLang } from "../../lib/lang/use-lang.ts"

export const OwnersDialog = ({
  avatar,
  team,
  item,
  label,
  showItemsColumn = true,
}: OwnersDialogProps) => {
  const { slug, id } = useParams()
  const { iykUser } = useIykUser()
  const lang = useLang()

  const iAmOwner = item.owners.find((owner) => owner.id === iykUser?.id)

  if (iAmOwner) {
    // Move the owner to the first position
    item.owners = [iAmOwner, ...item.owners.filter((owner) => owner.id !== iykUser?.id)]
  }

  return (
    <>
      <Dialog.Root>
        <Dialog.Content onOpenAutoFocus={(e) => e.preventDefault()} className="px-0 py-4">
          <Dialog.Title hidden></Dialog.Title>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2 px-4">
              <Link
                data-is-chip={Boolean(id)}
                to={`/creators/${slug}`}
                className="group flex gap-2 items-center w-fit"
              >
                <UI.Avatar
                  variant="square"
                  src={avatar}
                  className="group-data-[is-chip=true]:size-4 group-data-[is-chip=false]:size-7"
                />
                <span className="group-data-[is-chip=true]:text-xs">{team.name}</span>
              </Link>
              {id && (
                <Link
                  className={UI.classForLink({
                    className: "text-gray-12 no-underline hover:underline",
                  })}
                  to={`/creators/${slug}/items/${id}`}
                >
                  {item.name}
                </Link>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center px-4">
                <p className="flex-1 text-xs-upper">
                  {lang.terms.OWNER__OTHER}{" "}
                  <span className="text-gray-11">({item.owners.length})</span>
                </p>
                {showItemsColumn && <span className="text-xs-upper">{lang.terms.ITEM__OTHER}</span>}
              </div>
              <div className="flex flex-col max-h-[70dvh] overflow-y-auto px-2">
                {item.owners.map((itemOwner) => (
                  <Link
                    key={itemOwner.id}
                    to={`/profile/${itemOwner.username}`}
                    className="flex items-center hover:bg-gray-2 px-2 rounded-sm"
                  >
                    <div className="flex items-center gap-2 px-1 py-2 flex-1">
                      <UI.Avatar src={itemOwner.avatarUrl || undefined} className="size-8" />
                      <span className="text-sm">
                        {itemOwner.username}{" "}
                        {iAmOwner && itemOwner.id === iAmOwner.id ? (
                          <span className="text-gray-11 text-xs">
                            {toSentenceCase(lang.terms.YOU)}
                          </span>
                        ) : null}
                      </span>
                    </div>
                    {showItemsColumn && (
                      <span className="text-gray-11 text-xs">{itemOwner.quantity}</span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </Dialog.Content>
        <Dialog.Trigger asChild>
          <button type="button" className={UI.classForLink()}>
            {label}
          </button>
        </Dialog.Trigger>
      </Dialog.Root>
    </>
  )
}

export type OwnersDialogProps = {
  avatar: string
  label: React.ReactNode
  team: {
    id: number
    name: string
    urlSlug: string
    iconUrl: string | null
  }
  item: {
    name?: string
    owners: OwnerWithOwnershipData[]
  }
  showItemsColumn?: boolean
}

export function OwnersDialogFallback() {
  return <UI.Skeleton className="h-9 w-24" />
}
